import { Fragment, useRef, useState } from "react";
import {
  MIN_GROUP_SIZE,
  type AronNode,
  useAronWorkbenchStore,
} from "../../store/aronWorkbenchStore";
import { AronEditorGroup } from "./AronEditorGroup";

interface Props {
  node: AronNode;
  groupCount: number;
  onPopOut: (convId: string) => void;
  onDetach: (convId: string) => void;
}

/**
 * Renders the layout tree: a group draws its pane, a split draws its children
 * along its own axis with a draggable divider between them. Because every split
 * carries its own orientation, panes nested inside a pane divide only that pane
 * — dividing sideways inside a stacked layout never re-flows the page.
 */
export function AronSplitView({ node, groupCount, onPopOut, onDetach }: Props) {
  const activeGroupId = useAronWorkbenchStore((s) => s.activeGroupId);
  const setSizes = useAronWorkbenchStore((s) => s.setSizes);
  const containerRef = useRef<HTMLDivElement>(null);
  // Live sizes during a drag; committed to the (persisted) store on mouse-up so
  // localStorage isn't written on every pixel of movement.
  const [liveSizes, setLiveSizes] = useState<Record<string, number> | null>(null);

  if (node.type === "group") {
    return (
      <AronEditorGroup
        group={node}
        isActiveGroup={node.id === activeGroupId}
        groupCount={groupCount}
        onPopOut={onPopOut}
        onDetach={onDetach}
      />
    );
  }

  const horizontal = node.orientation === "row";

  const startDrag = (e: React.MouseEvent, i: number) => {
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const totalPx = horizontal ? rect.width : rect.height;
    if (totalPx <= 0) return;
    const sum = node.children.reduce((acc, c) => acc + c.size, 0);
    const a = node.children[i];
    const b = node.children[i + 1];
    const startPos = horizontal ? e.clientX : e.clientY;
    const min = MIN_GROUP_SIZE * sum;

    const compute = (pos: number) => {
      const delta = ((pos - startPos) / totalPx) * sum;
      const aSize = Math.max(min, Math.min(a.size + delta, a.size + b.size - min));
      return { [a.id]: aSize, [b.id]: a.size + b.size - aSize };
    };
    const onMove = (ev: MouseEvent) =>
      setLiveSizes(compute(horizontal ? ev.clientX : ev.clientY));
    const onUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setSizes(compute(horizontal ? ev.clientX : ev.clientY));
      setLiveSizes(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      ref={containerRef}
      className={`flex min-h-0 min-w-0 flex-1 ${horizontal ? "flex-row" : "flex-col"}`}
    >
      {node.children.map((child, i) => (
        <Fragment key={child.id}>
          {i > 0 && (
            <div
              role="separator"
              aria-orientation={horizontal ? "vertical" : "horizontal"}
              aria-label="Przeciągnij, aby zmienić podział"
              onMouseDown={(e) => startDrag(e, i - 1)}
              className={`shrink-0 bg-gray-200 transition-colors hover:bg-red-400 dark:bg-gray-700 ${
                horizontal ? "w-1 cursor-col-resize" : "h-1 cursor-row-resize"
              }`}
            />
          )}
          <div
            style={{ flexGrow: liveSizes?.[child.id] ?? child.size, flexBasis: 0 }}
            className="flex min-h-0 min-w-0"
          >
            <AronSplitView
              node={child}
              groupCount={groupCount}
              onPopOut={onPopOut}
              onDetach={onDetach}
            />
          </div>
        </Fragment>
      ))}
    </div>
  );
}
